import {
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  FormGroup,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { withStyles, withTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { PropTypes } from 'prop-types';
import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { isLoaded } from 'react-redux-firebase';
import { compose } from 'redux';
import { updateUi as updateUiAction } from '../../redux/actions/uiActions';
import { addUser as addUserAction } from '../../redux/actions/userActions';
import LoadingButton from '../widgets/LoadingButton';
import LogoTooltip from './../widgets/LogoTooltip';
import cidLogo from '../../assets/images/logo/monograma_cid.png';
import fidLogo from '../../assets/images/logo/monograma_flyid.png';

const styles = (theme) => ({
  grow: {
    flexGrow: 1,
  },
  margin: {
    marginBottom: theme.spacing(2),
  },
  button: {
    margin: theme.spacing(2, 0, 2, 0),
  },
  title: {
    color: theme.palette.other.grey.dark,
  },
  container: {
    ...theme.container(2),
    marginLeft: 0,
    maxWidth: '800px',
  },
  hidden: {
    display: 'none',
  },
  countidIconRootClass: {
    backgroundColor: theme.palette.primary.main,
  },
  flyidIconRootClass: {
    backgroundColor: theme.palette.primary.dark,
  },
});

const isAllowedEmailCharacter = (str) => /^[a-zA-Z0-9+_.-]*$/.test(str);
const getFakeEmailDomain = (company) => `@${company?.replace(new RegExp('_', 'g'), '.')}.flyid.com`;
const replaceSpacesByDots = (str) => str?.replace(new RegExp('\\s', 'g'), '.');

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 10 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
  getContentAnchorEl: () => null,
};

const initialUserData = {
  firstName: '',
  lastName: '',
  employeeId: '',
  permissions: [],
  authDomains: [],
  email: '',
  password: '',
  confirmation: '',
  company: '',
  lacksInstitutionalEmail: false,
};
const pilot = 'pilot';
const checker = 'checker';
const assistant = 'assistant';
const permissionsRef = [pilot, checker, assistant];

class AddUser extends Component {
  state = {
    ...initialUserData,
    // UI Flags
    missingPermissions: false,
    missingDomains: false,
    showConfirmation: false,
    init: false,
  };

  static getDerivedStateFromProps(props, state) {
    if (!state.init) {
      if (isLoaded(props.profile) && props.profile.company) {
        return {
          ...state,
          company: props.profile.company,
          init: true,
        };
      }
    }
    return null;
  }

  isArrayEmpty = (arr) => {
    return !arr || !arr.length;
  };

  handleSubmit = (e) => {
    e.preventDefault();
    const { addUser, profile } = this.props;

    const userData = {};
    Object.keys(initialUserData).forEach((key) => (userData[key] = this.state[key]));
    userData.company = profile.company;

    if (this.state.lacksInstitutionalEmail) {
      userData.email = userData.email + getFakeEmailDomain(this.state.company);
    }

    let missingDomains = this.isArrayEmpty(userData.authDomains);
    let missingPermissions = this.isArrayEmpty(userData.permissions);
    if (missingDomains || missingPermissions) {
      this.setState({
        missingPermissions,
        missingDomains,
      });
      return;
    }

    // 6 digit password check if pilot
    const userPerm = userData.permissions;
    let isPilotOrChecker = userPerm.includes(pilot) || userPerm.includes(checker);
    if (userData.password) {
      if (isPilotOrChecker && !/^\d{6}$/.test(userData.password)) {
        this.props.updateUi({
          snackbar: {
            message: this.props.intl.formatMessage({ id: 'err.pilotPassword' }),
            severity: 'error',
            show: true,
          },
        });
        return;
      }
      if (userData.password !== userData.confirmation) {
        this.props.updateUi({
          snackbar: {
            message: this.props.intl.formatMessage({ id: 'err.pwNoMatch' }),
            severity: 'error',
            show: true,
          },
        });
        return;
      }
    }

    permissionsRef.forEach((perm) => {
      userData[perm] = userData.permissions.includes(perm);
    });
    delete userData.permissions;

    addUser(userData);
  };

  handleCheckboxChange = (event) => {
    const { firstName, lastName } = this.state;
    const data = { [event.target.name]: event.target.checked };
    if (event.target.name === 'lacksInstitutionalEmail') {
      data.email = Boolean(event.target.checked && firstName && lastName)
        ? replaceSpacesByDots(`${firstName.toLowerCase()}.${lastName.toLowerCase()}`)
        : '';
    }
    this.setState(data);
  };

  handleChange = (e) => {
    let showConfirmation =
      e.target.name === 'password' ? (e.target.value.length > 0 ? true : false) : this.state.showConfirmation;

    switch (e.target.name) {
      case 'email': {
        if (this.state.lacksInstitutionalEmail && !isAllowedEmailCharacter(e.target.value)) {
          this.props.updateUi({
            snackbar: {
              message: this.props.intl.formatMessage({ id: 'err.invalidEmailCharacter' }),
              severity: 'error',
              show: true,
            },
          });
          return;
        }
        break;
      }
      default:
        break;
    }

    this.setState({
      [e.target.name]: e.target.value,
      showConfirmation,
    });
  };

  render() {
    const { classes, profile, ui, intl } = this.props;
    const authDomains = profile ? profile.authDomains : [];

    return (
      <Container className={classes.container}>
        <form onSubmit={this.handleSubmit}>
          <Typography variant="h4" className={clsx(classes.margin, classes.title)}>
            {intl.formatMessage({ id: 'addUsr.title' })}
          </Typography>
          <TextField
            fullWidth
            required
            id="firstName"
            name="firstName"
            type="text"
            label={intl.formatMessage({ id: 'firstName' })}
            value={this.state.firstName}
            onChange={this.handleChange}
            className={classes.margin}
            autoFocus
          />
          <TextField
            fullWidth
            required
            id="lastName"
            name="lastName"
            type="text"
            label={intl.formatMessage({ id: 'lastName' })}
            value={this.state.lastName}
            onChange={this.handleChange}
            className={classes.margin}
          />
          <TextField
            fullWidth
            required
            id="employeeId"
            name="employeeId"
            type="text"
            label={intl.formatMessage({ id: 'employeeId' })}
            value={this.state.employeeId}
            onChange={this.handleChange}
            className={classes.margin}
          />
          <FormControl required fullWidth className={classes.margin} error={this.state.missingPermissions}>
            <InputLabel id="permissions-label">{intl.formatMessage({ id: 'permissions' })}</InputLabel>
            <Select
              labelId="permissions-label"
              id="permissions"
              name="permissions"
              multiple
              value={this.state.permissions}
              onChange={this.handleChange}
              onClose={() => {
                if (this.state.missingPermissions) {
                  this.setState({
                    missingPermissions: this.isArrayEmpty(this.state.permissions),
                  });
                }
              }}
              input={<Input />}
              renderValue={(selected) => selected.map((perm) => intl.formatMessage({ id: perm })).join(', ')}
              MenuProps={MenuProps}
              autoWidth
            >
              {permissionsRef.map((perm) => (
                <MenuItem key={perm} value={perm}>
                  <Checkbox checked={this.state.permissions.indexOf(perm) > -1} />
                  <ListItemText primary={intl.formatMessage({ id: perm })} />
                  {perm === checker && (
                    <LogoTooltip
                      tooltip={intl.formatMessage({ id: 'grantsCountidAccess' })}
                      logo={cidLogo}
                      iconRootClass={classes.countidIconRootClass}
                    />
                  )}
                  {perm === pilot && (
                    <LogoTooltip
                      tooltip={intl.formatMessage({ id: 'grantsFlyidAccess' })}
                      logo={fidLogo}
                      iconRootClass={classes.flyidIconRootClass}
                    />
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl required fullWidth className={classes.margin} error={this.state.missingDomains}>
            <InputLabel id="authdomains-label">{intl.formatMessage({ id: 'authDomains' })}</InputLabel>
            <Select
              labelId="authdomains-label"
              id="authdomains"
              name="authDomains"
              multiple
              value={this.state.authDomains}
              onChange={this.handleChange}
              onClose={() => {
                if (this.state.missingDomains) {
                  this.setState({
                    missingDomains: this.isArrayEmpty(this.state.authDomains),
                  });
                }
              }}
              input={<Input />}
              renderValue={(selected) => selected.join(', ')}
              MenuProps={MenuProps}
              autoWidth
            >
              {authDomains.map((domain) => (
                <MenuItem key={domain} value={domain}>
                  <Checkbox checked={this.state.authDomains.indexOf(domain) > -1} />
                  <ListItemText primary={domain} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    name="lacksInstitutionalEmail"
                    checked={this.state.lacksInstitutionalEmail}
                    onChange={this.handleCheckboxChange}
                  />
                }
                label={
                  <Typography variant="body2">
                    {intl.formatMessage({ id: 'addUsr.lacksInstitutionalEmail' })}
                  </Typography>
                }
              />
            </FormGroup>
          </FormControl>
          <TextField
            fullWidth={!this.state.lacksInstitutionalEmail}
            inputProps={{ autoComplete: 'off' }}
            required
            id="email"
            name="email"
            type={this.state.lacksInstitutionalEmail ? 'text' : 'email'}
            label={intl.formatMessage({ id: 'addUsr.email' })}
            value={this.state.email}
            onChange={this.handleChange}
            className={classes.margin}
          />
          {this.state.lacksInstitutionalEmail && (
            <TextField
              disabled={true}
              label=" "
              value={getFakeEmailDomain(this.state.company)}
              className={classes.margin}
            />
          )}
          <TextField
            fullWidth
            inputProps={{ autoComplete: 'new-password' }}
            id="password"
            name="password"
            type="password"
            label={intl.formatMessage({ id: 'addUsr.pw' })}
            value={this.state.pin}
            onChange={this.handleChange}
            className={classes.margin}
          />
          <TextField
            fullWidth
            inputProps={{ autoComplete: 'new-password' }}
            id="confirmation"
            name="confirmation"
            type="password"
            label={intl.formatMessage({ id: 'addUsr.pwconf' })}
            value={this.state.confirmation}
            onChange={this.handleChange}
            required={this.state.showConfirmation}
            className={clsx(classes.margin, this.state.showConfirmation ? null : classes.hidden)}
          />

          <LoadingButton
            isLoading={ui.loadingButton.isAddUserLoading}
            content={intl.formatMessage({ id: 'submit' })}
            type="submit"
          />
        </form>
      </Container>
    );
  }
}

AddUser.propTypes = {
  auth: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
};

export default compose(
  connect(
    ({ firebase: { auth, profile }, ui }) => ({
      auth,
      profile,
      ui,
    }),
    (dispatch, ownProps) => ({
      ...ownProps,
      addUser: (userData) => dispatch(addUserAction(userData)),
      updateUi: (data) => dispatch(updateUiAction(data)),
    })
  ),
  withStyles(styles),
  withTheme,
  injectIntl
)(AddUser);
